import './app.css';
import React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { AppearanceListener } from '../../common/resources/client/admin/appearance/commands/appearance-listener';
import { CommonProvider } from '../../common/resources/client/core/common-provider';
import { AuthRoutes } from '../../common/resources/client/auth/auth-routes';
import { AuthRoute } from '../../common/resources/client/auth/guards/auth-route';
import { FullPageLoader } from '../../common/resources/client/ui/progress/full-page-loader';
import { BillingRoutes } from '../../common/resources/client/billing/billing-routes';
import { NotificationRoutes } from '../../common/resources/client/notifications/notification-routes';
import { CookieNotice } from '../../common/resources/client/ui/cookie-notice/cookie-notice';
import { ContactUsPage } from '../../common/resources/client/contact/contact-us-page';
import { PointPage } from '../../common/resources/client/points/point-page';
import { CustomPageLayout } from '../../common/resources/client/custom-page/custom-page-layout';
import { ToastContainer } from '../../common/resources/client/ui/toast/toast-container';
import { useAuth } from '../../common/resources/client/auth/use-auth';
import { EmailVerificationPage } from '../../common/resources/client/auth/ui/email-verification-page/email-verification-page';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { rootEl } from '../../common/resources/client/core/root-el';
import { useSettings } from '../../common/resources/client/core/settings/use-settings';
import { getBootstrapData } from '../../common/resources/client/core/bootstrap-data/use-backend-bootstrap-data';
import { NotFoundPage } from '../../common/resources/client/ui/not-found-page/not-found-page';
import { Playlist } from '../../resources/client/web-player/playlists/playlist';
import { Track, TRACK_MODEL } from '../../resources/client/web-player/tracks/track';
import { ALBUM_MODEL } from '../../resources/client/web-player/albums/album';
import { ARTIST_MODEL } from '../../resources/client/web-player/artists/artist';
import { Repost } from '../../resources/client/web-player/reposts/repost';
import { UserProfile } from '../../resources/client/web-player/user-profile/user-profile';
import { UserLink } from '../../resources/client/web-player/user-profile/user-link';
import { UserArtist } from '../../resources/client/web-player/user-profile/user-artist';
import { DynamicHomepage } from '../../common/resources/client/ui/dynamic-homepage';
import { LandingPage } from '../../resources/client/landing-page/landing-page';
import { LandingPageContent } from '../../resources/client/landing-page/landing-page-content';
import { useAppearanceEditorMode } from '../../common/resources/client/admin/appearance/commands/use-appearance-editor-mode';
import { ignoredSentryErrors } from '../../common/resources/client/errors/ignored-sentry-errors';
import { DialogStoreOutlet } from '../../common/resources/client/ui/overlays/store/dialog-store-outlet';

const AdminRoutes = React.lazy(() => import('../../common/resources/client/admin/admin-routes'));
const WebPlayerRoutes = React.lazy(
  () => import('../../resources/client/web-player/web-player-routes')
);
const BackstageRoutes = React.lazy(
  () => import('../../resources/client/web-player/backstage/backstage-routes')
);
const SwaggerApiDocs = React.lazy(
  () => import('../../common/resources/client/swagger/swagger-api-docs-page')
);

declare module '../../common/resources/client/core/settings/settings' {
  interface Settings {
    spotify_is_setup?: boolean;
    lastfm_is_setup?: boolean;
    artist_provider?: string;
    album_provider?: string;
    search_provider?: string;
    artist_bio_provider?: string;
    player?: {
      show_upload_btn?: boolean;
      default_volume?: number;
      hide_video_button?: boolean;
      hide_radio_button?: boolean;
      track_comments?: boolean;
      seekbar_type?: 'waveform' | 'bar';
      enable_repost?: boolean;
      hide_queue?: boolean;
      hide_video?: boolean;
      hide_lyrics?: boolean;
      enable_download?: boolean;
      show_become_artist_btn?: boolean;
      default_artist_view?: 'list' | 'grid';
      mobile?: {
        auto_open_overlay?: boolean;
      };
    };
    artistPage: {
      tabs: { id: number; active: boolean }[];
      showDescription?: boolean;
    };
    youtube?: {
      suggested_quality?: YT.SuggestedVideoQuality;
      search_method?: string;
    };
    homepage: {
      type: string;
      value?: string;
      pricing?: boolean;
      appearance: LandingPageContent;
      trending?: boolean;
    };
    ads?: {
      general_top?: string;
      general_bottom?: string;
      artist_top?: string;
      artist_bottom?: string;
      album_above?: string;
      disable?: boolean;
    };
  }
}

declare module '../../common/resources/client/auth/user' {
  interface User {
    uploaded_tracks: Track[];
    followed_users?: this[];
    followers_count?: number;
    followed_users_count?: number;
    followers?: this[];
    playlists: Playlist[];
    reposts?: Repost[];
    profile?: UserProfile;
    links?: UserLink[];
    artists?: UserArtist[];
  }
}

declare module '../../common/resources/client/core/bootstrap-data/bootstrap-data' {
  interface BootstrapData {
    playlists?: Playlist[];
    artists: {
      id: number;
      name: string;
      image_small?: string;
      role: string;
    }[];
    likes?: {
      [TRACK_MODEL]: Record<number, boolean>;
      [ALBUM_MODEL]: Record<number, boolean>;
      [ARTIST_MODEL]: Record<number, boolean>;
    };
    reposts?: {
      [TRACK_MODEL]: Record<number, boolean>;
      [ALBUM_MODEL]: Record<number, boolean>;
    };
  }
}

const sentryDsn = getBootstrapData().settings.logging.sentry_public;
const version = getBootstrapData().settings.version;
if (sentryDsn && import.meta.env.PROD) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    ignoreErrors: ignoredSentryErrors,
    release: version,
  });
}

const root = createRoot(rootEl);
root.render(
  <CommonProvider>
    <Router />
  </CommonProvider>
);

function Router() {
  const {
    billing,
    notifications,
    require_email_confirmation,
    api,
    html_base_uri,
    homepage,
  } = useSettings();
  const { isAppearanceEditorActive } = useAppearanceEditorMode();
  const { user, hasPermission } = useAuth();

  if (user != null && require_email_confirmation && !user.email_verified_at) {
    return (
      <BrowserRouter>
        <ToastContainer />
        <Routes>
          <Route path="*" element={<EmailVerificationPage />} />
        </Routes>
        <DialogStoreOutlet />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter basename={html_base_uri}>
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      <Routes>
        <Route
          path="/*"
          element={
            <AuthRoute requireLogin={false} permission="music.view">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <WebPlayerRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />

        <Route
          path="backstage/*"
          element={
            <AuthRoute>
              <React.Suspense fallback={<FullPageLoader screen />}>
                <BackstageRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        {!homepage?.type.startsWith('channel') &&
          (user == null || isAppearanceEditorActive) && (
            <Route
              path="/"
              element={
                <DynamicHomepage homepageResolver={() => <LandingPage />} />
              }
            />
          )}
        <Route path="points" element={<PointPage />} />
        <Route
          path="/admin/*"
          element={
            <AuthRoute permission="admin.access">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <AdminRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        {AuthRoutes}
        {billing.enable && BillingRoutes}
        {notifications.integrated && NotificationRoutes}
        {api?.integrated && hasPermission('api.access') && (
          <Route
            path="api-docs"
            element={
              <React.Suspense fallback={<FullPageLoader screen />}>
                <SwaggerApiDocs />
              </React.Suspense>
            }
          />
        )}
        <Route path="contact" element={
          <React.Suspense fallback={<FullPageLoader screen />}>
            <ContactUsPage />
          </React.Suspense>
        } />
        <Route path="pages/:pageSlug" element={<CustomPageLayout />} />
        <Route path="pages/:pageId/:pageSlug" element={<CustomPageLayout />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <DialogStoreOutlet />
    </BrowserRouter>
  );
}
